import {z} from 'zod';

/**
 * The languages the interface and the mail are written in. Adding one here is the first
 * step; `packages/frontend/src/i18n` and `packages/core/src/email/copy.ts` are both
 * checked against this list, so the suite says exactly what is still missing.
 */
export const LOCALES = ['nl', 'en'] as const;

export type Locale = (typeof LOCALES)[number];

/**
 * Dutch, because the people dispatching assets on this grid work in Dutch. It is what an
 * account starts on and what a mail falls back to when nobody has expressed a preference.
 */
export const DEFAULT_LOCALE: Locale = 'nl';

export const localeSchema = z.enum(LOCALES);

const isLocale = (value: string): value is Locale => (LOCALES as readonly string[]).includes(value);

/**
 * Narrows anything that claims to be a language — a browser's `navigator.language`, a
 * column read back from an older row, a Cognito attribute — to one we can actually
 * render, or `null` when it is none of them. `nl-NL` and `NL` are both Dutch.
 *
 * Separate from `resolveLocale` because "we cannot render this" and "the reader did not
 * say" are different answers: the frontend falls through from a stale stored preference
 * to the browser's own language, which a bare default would hide.
 */
export const parseLocale = (candidate: string | null | undefined): Locale | null => {
  const base = candidate?.trim().toLowerCase().split(/[-_]/)[0] ?? '';
  return isLocale(base) ? base : null;
};

/**
 * The same narrowing, with the default standing in for anything unrecognised. Nothing
 * here throws: no mail is worth failing over the language it would have been written in.
 */
export const resolveLocale = (candidate: string | null | undefined): Locale => parseLocale(candidate) ?? DEFAULT_LOCALE;
